import { useEffect, useState } from "react";
import { api } from "./api";

const cache = {};

export function fetchPhaseFeatures(key = "anon", force = false) {
  if (!cache[key] || force) {
    cache[key] = api.get("/admin/phase-features").then((r) => r.data).catch(() => ({ phases: [] }));
  }
  return cache[key];
}

export function usePhaseFeature(n, userId) {
  const [state, setState] = useState(null);
  useEffect(() => {
    let live = true;
    fetchPhaseFeatures(userId || "anon").then((d) => {
      if (!live) return;
      const p = (d.phases || []).find((x) => x.phase === n);
      setState({ enabled: !!p?.enabled, role_key: d.role_key });
    });
    return () => { live = false; };
  }, [n, userId]);
  return state;
}

// Standalone module switches (modswitch: "communications" = B12, "closeout" = B13). GET /modules/:key is the
// registry truth for this workspace; any error -> treated as inactive (the server gates every route anyway).
const modCache = {};
export function fetchModuleActive(key, userId = "anon", force = false) {
  const c = `${userId}:${key}`;
  if (!modCache[c] || force) {
    modCache[c] = api.get(`/modules/${key}`).then((r) => r.data?.state === "active").catch(() => false);
  }
  return modCache[c];
}

export function useModuleActive(key, userId) {
  const [on, setOn] = useState(null);
  useEffect(() => {
    let live = true;
    fetchModuleActive(key, userId || "anon").then((v) => live && setOn(v));
    return () => { live = false; };
  }, [key, userId]);
  return on;
}

// Communications nav: ON -> Notifications (everyone) + Delivery recovery (Founder/Admin); OFF -> Founder/Admin see
// "Available to Activate" (module status page); everyone else: hidden. `comms` = { enabled } or null while loading.
export function commsNavEntries({ comms, isAdminRole }) {
  if (!comms) return [];
  if (comms.enabled) {
    const out = [{ key: "notifications-center", label: "Notifications", to: "/notifications" }];
    if (isAdminRole) out.push({ key: "delivery-recovery", label: "Delivery recovery", to: "/admin/delivery-recovery" });
    return out;
  }
  return isAdminRole ? [{ key: "communications", label: "Communications", to: "/phase/communications", badge: "Available to Activate" }] : [];
}

export const ARCHITECT_ROLES = ["OWNER_SUPER_ADMIN", "ADMINISTRATOR", "ARCHITECT"];
export const FINANCE_ROLES = ["OWNER_SUPER_ADMIN", "BOOKKEEPER_ACCOUNTANT", "ADMINISTRATOR"];
export const FIELD_ROLES = ["OWNER_SUPER_ADMIN", "ADMINISTRATOR", "SUPERINTENDENT", "FIELD_SUPERVISOR", "PROJECT_MANAGER"];
// B11 workforce: staff + subcontractor/vendor company users (the server limits each to its own scope).
export const WORKFORCE_ROLES = [...FIELD_ROLES, "BOOKKEEPER_ACCOUNTANT", "EMPLOYEE", "SUBCONTRACTOR", "VENDOR_SUPPLIER", "CONTRACTOR_1099"];

// B12 menu: Finance operations (/finance, B10) and Workforce (/workforce, B11). Module ON + allowed role -> the page;
// module OFF -> Founder/Administrator see "Available to Activate" (module status page); everyone else: hidden.
// Phase state comes from the unified module registry (GET /admin/phase-features reads it server-side).
export function moduleNavEntries({ phase4, phase5, role, isAdminRole }) {
  const out = [];
  const add = (feature, roles, item, moduleKey) => {
    if (!feature) return;
    if (feature.enabled && roles.includes(role)) out.push(item);
    else if (!feature.enabled && isAdminRole) out.push({ ...item, to: `/phase/${moduleKey}`, badge: "Available to Activate" });
  };
  add(phase5, FINANCE_ROLES, { key: "finance-ops", label: "Finance operations", to: "/finance" }, "financials");
  add(phase4, WORKFORCE_ROLES, { key: "workforce", label: "Workforce", to: "/workforce" }, "field_ops");
  return out;
}

export const ARCH_DISCLAIMER =
  "Design collaboration record only. This is NOT licensed professional architectural approval, NOT code-compliance certification and NOT engineering sign-off. Nothing here is signed, sealed or permit-ready.";

export const REVIEW_LABELS = {
  draft: "Draft",
  awaiting_review: "Awaiting review",
  design_review_approved: "Design review approved",
  changes_requested: "Changes requested",
};
